import ApiClient from '@afosto/api/ApiClient';

class Items {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Add items
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Adds items to the stack that was previously created
   * @returns {Promise}
   */
  addItems(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Attach warehouse item onto an order item
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set the warehouse item on a product.
   * @returns {Promise}
   */
  attachWarehouseItems(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/attach`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Remove the allocation from an order item
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set the warehouse item on a product.
   * @returns {Promise}
   */
  cancelOrderItems(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/cancel`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * From search
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Add items to order based on item search
   * @returns {Promise}
   */
  confirmItems(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/search`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Delete items
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Delete items by ID
   * @returns {Promise}
   */
  deleteItems(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items`,
      method: 'delete',
      data: body,
      ...options,
    });
  }

  /**
   * List items
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.stackId]
   * @param {Object} options - The additional request options
   * @description Returns a list of items
   * @returns {Promise}
   */
  listItems(id, query = {}, options = {}) {
    const params = {
      stack_id: query.stackId,
    };

    return this.apiClient.request({
      url: `/odr/orders/${id}/items`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * List items with routing
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of items
   * @returns {Promise}
   */
  listItemsRouting(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/routing`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Search for items
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Returns a list items
   * @returns {Promise}
   */
  searchGroups(body, options = {}) {
    return this.apiClient.request({
      url: `/odr/search/orders/items`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Search for items with routing
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Returns a list items with routing
   * @returns {Promise}
   */
  searchGroupsWithAttach(body, options = {}) {
    return this.apiClient.request({
      url: `/odr/search/orders/items/attach`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Search for items with routing
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Returns a list items with routing
   * @returns {Promise}
   */
  searchGroupsWithRouting(body, options = {}) {
    return this.apiClient.request({
      url: `/odr/search/orders/items/routing`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Set claim
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set an claim on a order item
   * @returns {Promise}
   */
  setClaim(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/claim`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set route
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Define a WMS route for a set of order items
   * @returns {Promise}
   */
  setRoute(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/orders/${id}/items/routing`,
      method: 'put',
      data: body,
      ...options,
    });
  }
}

export const items = new Items();

export default Items;
